import { Star, ExternalLink, FileText, Quote } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal } from "../components/Motion";
import { PageHeader, CtaBand } from "../components/Detail";
import { REVIEWS } from "../data/reviews";

const Stars = ({ testId }) => (
  <span className="flex gap-0.5" aria-hidden="true" data-testid={testId}>
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} className={`h-4 w-4 ${i < 4 ? "fill-gold text-gold" : "fill-gold/40 text-gold/40"}`} />
    ))}
  </span>
);

export default function Feedback() {
  const { t } = useLanguage();
  const f = t.feedback;
  return (
    <main id="main-content">
      <PageHeader overline={f.pageOverline} title={f.pageTitle} sub={f.pageSub} overlineTestId="feedback-page-overline" />

      <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-16 lg:px-12">
        <div className="grid gap-4 md:grid-cols-3" data-testid="feedback-sources-grid">
          <Reveal delay={0.02}>
            <a
              href="https://www.google.com/search?q=Naciye+Rafiq-Arli+Hypnosecoaching+Hamburg+Rezensionen"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="feedback-source-google"
              className="group flex h-full flex-col rounded-3xl border border-warmline bg-white p-7 transition-all hover:-translate-y-1 hover:border-teal"
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate2">Google</p>
              <p className="mt-3 font-display text-4xl font-semibold tracking-tight text-petrol">4,8/5</p>
              <Stars testId="feedback-google-stars" />
              <p className="mt-2 flex-1 text-sm text-slate2">{t.common.rating.split("·")[1]?.trim() || "32 Rezensionen"}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-teal-dark">
                {f.googleCta}
                <ExternalLink className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.05}>
            <a
              href="https://www.jameda.de/naciye-rafiq-arli/heilpraktiker-fuer-psychotherapie/hamburg"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="feedback-source-jameda"
              className="group flex h-full flex-col rounded-3xl border border-warmline bg-white p-7 transition-all hover:-translate-y-1 hover:border-teal"
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate2">Jameda</p>
              <p className="mt-3 font-display text-4xl font-semibold tracking-tight text-petrol">11</p>
              <p className="mt-2 flex-1 text-sm text-slate2">{f.jamedaCount}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-teal-dark">
                {f.jamedaCta}
                <ExternalLink className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.08}>
            <a
              href="#berichte"
              data-testid="feedback-source-reports"
              className="group flex h-full flex-col rounded-3xl border border-warmline bg-white p-7 transition-all hover:-translate-y-1 hover:border-teal"
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate2">{f.reportsTitle}</p>
              <p className="mt-3 font-display text-4xl font-semibold tracking-tight text-petrol">{REVIEWS.length}</p>
              <p className="mt-2 flex-1 text-sm text-slate2">{f.reportsCount}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-teal-dark">
                {f.reportsCta}
                <FileText className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            </a>
          </Reveal>
        </div>

        <Reveal className="mt-14">
          <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-teal-dark">{f.shortTitle}</p>
        </Reveal>
        <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="feedback-shortquotes-grid">
          {t.home.testimonials.items.map((item, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <figure className="flex h-full flex-col rounded-3xl border border-warmline bg-sand p-7" data-testid={`feedback-quote-${i}`}>
                <Quote className="h-6 w-6 text-gold" aria-hidden="true" />
                <blockquote className="mt-3 flex-1 font-display text-lg font-medium leading-relaxed text-petrol">
                  {item.quote}
                </blockquote>
              </figure>
            </Reveal>
          ))}
        </div>

        <div id="berichte" className="scroll-mt-24 pt-14">
          <Reveal>
            <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-teal-dark" data-testid="feedback-reports-title">
              {f.reportsTitle}
            </p>
            {f.reportsNote && (
              <p className="mt-3 max-w-2xl text-sm italic text-slate2" data-testid="feedback-reports-note">
                {f.reportsNote}
              </p>
            )}
          </Reveal>
          <div className="mt-8 columns-1 gap-4 lg:columns-2" data-testid="feedback-reports-list">
            {REVIEWS.map((review) => (
              <Reveal key={review.id} delay={0.02} className="mb-4 break-inside-avoid">
                <article className="rounded-3xl border border-warmline bg-white p-7" data-testid={`feedback-report-${review.id}`}>
                  <header className="flex items-center justify-between gap-3">
                    <p className="font-display text-lg font-semibold tracking-tight text-petrol">
                      {f.fromLabel} {review.author}
                    </p>
                    <Stars testId={`feedback-report-stars-${review.id}`} />
                  </header>
                  <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate2">
                    {review.text.split("\n\n").map((para, j) => (
                      <p key={j}>{para}</p>
                    ))}
                  </div>
                  <footer className="mt-5 border-t border-warmline pt-3 text-xs text-slate2">
                    {f.sourceLabel}: {f.sourceWebsite}
                  </footer>
                </article>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-12">
          <CtaBand testId="feedback-cta-band" ctaTestId="feedback-whatsapp-cta" />
        </div>
      </div>
    </main>
  );
}
